import { getUserProfile, updateUserProfile } from '@app/api/user.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { notificationController } from '@app/controllers/notificationController';
import { Col, Form, Row, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { UserDTO } from '@app/models/user/UserDTO';
import { DropdownOption } from '@app/models/DropdownOption';
import { RegularCustomerBusinessInfo } from './components/RegularCustomerBusinessInfo';
import { UserInfo } from './components/UserInfo';
import { authUserService } from '@app/services/authUser.service';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { useTranslation } from 'react-i18next';

type AccountInformationProps = Record<string, unknown>;

const AccountInformation: React.FC<AccountInformationProps> = () => {
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const { t } = useTranslation();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [accountTypes, setAccountTypes] = useState<DropdownOption[]>([]);
  const [companyTypes, setCompanyTypes] = useState<DropdownOption[]>([]);
  const [isCustomer, setIsCustomer] = useState<boolean>(false);
  const [isRegularCustomer, setIsRegularCustomer] = useState<boolean>(false);
  const [isBusinessRegularCustomer, setIsBusinessRegularCustomer] = useState<boolean>(false);
  const [isBusinessRegularCustomerRegistered, setIsBusinessRegularCustomerRegistered] = useState<boolean>(false);
  const [profile, setProfile] = useState<UserDTO | null>(null);
  const setApiData = (responseData: UserDTO) => {
    const info = responseData?.user?.customer?.additional_info;

    setIsCustomer(responseData?.user?.usertype == 'CUSTOMER');
    setIsRegularCustomer(responseData?.user?.customer?.customer_type == 'REGULAR');
    setIsBusinessRegularCustomer(info?.business_type == 'BUSINESS');
    setAccountTypes(responseData?.business_type_options ?? []);
    setCompanyTypes(responseData?.company_type_options ?? []);
    setIsBusinessRegularCustomerRegistered(!!info?.business_reg_no?.length);
    setProfile(responseData);
  };

  useEffect(() => {
    if (!profile) return;

    const customer = profile?.user?.customer;
    const info = customer?.additional_info;
    const isView = mode === 'view';
    const registered = info?.has_br === 'YES';

    form.setFieldsValue({
      first_name: customer?.first_name,
      last_name: customer?.last_name,
      email: profile?.user?.email,
      username: profile?.user?.username,
      dob: customer?.dob,
      contact_no: customer?.contact_no,
      address: customer?.address,
      nic: customer?.nic,
      customer_type: customer?.customer_type,
      business_name: info?.business_name,
      business_reg_no: info?.business_reg_no,
      business_address: info?.business_address,
      business_type: isView
        ? profile?.business_type_options?.find((e) => e.value == info?.business_type)?.label
        : info?.business_type,
      company_type: isView
        ? profile?.company_type_options?.find((e) => e.value == info?.company_type)?.label
        : info?.company_type,
      is_registered: isView ? (registered ? 'Yes' : 'No') : registered,
    });
  }, [profile, mode, form]);

  const getUserProfileData = () => {
    const user = authUserService.getUser();
    const id = user?.id ?? 0;

    getUserProfile(id)
      .then((response) => {
        console.log(response);
        setApiData(response);
      })
      .catch((error) => {
        notificationController.error({ message: error.message });
      });
  };

  useEffect(() => {
    getUserProfileData();
  }, []);

  const close = () => {
    setMode('view');
  };

  useEffect(() => {
    if (mode === 'edit') {
      addHeaderButton(
        'Save',
        <BaseButton type="primary" size="small" onClick={() => form.submit()}>
          Save
        </BaseButton>,
      );
      addHeaderButton(
        'Cancel',
        <BaseButton htmlType="button" type="primary" size="small" onClick={close}>
          Cancel
        </BaseButton>,
      );
    } else {
      addHeaderButton(
        'Edit',
        <BaseButton type="primary" size="small" onClick={() => setMode('edit')}>
          Edit
        </BaseButton>,
      );

      removeHeaderButton('Save');
      removeHeaderButton('Cancel');
    }

    return () => {
      removeHeaderButton('Save');
      removeHeaderButton('Cancel');
      removeHeaderButton('Edit');
    };
  }, [mode, addHeaderButton, removeHeaderButton]);

  const onFinish = (values: any) => {
    const user = authUserService.getUser();
    const id = user?.id ?? 0;

    const { is_registered, ...rest } = values;
    const hasBr = is_registered === true || is_registered === 'Yes';

    const req = {
      ...rest,
      has_br: hasBr ? 'YES' : 'NO',
      customer_type: profile?.user?.customer?.customer_type ?? user?.customer?.customer_type,
    };
    if (!hasBr) delete req.business_reg_no;

    updateUserProfile(id, req)
      .then(() => {
        notificationController.success({ message: 'Profile updated successfully!' });
        setMode('view');
        getUserProfileData();
      })
      .catch((error) => notificationController.error({ message: error.message }));
  };

  return (
    <BaseCard
      title="Account Information"
      // extra={
      //   mode == 'view' && (
      //     <BaseButton type="primary" onClick={() => setMode('edit')}>
      //       Edit
      //     </BaseButton>
      //   )
      // }
    >
      <Form
        form={form}
        name="accountForm"
        labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
        size="small"
        onFinish={onFinish}
        onFinishFailed={({ errorFields }) => {
          console.warn('Validation failed:', errorFields);
          notificationController.error({ message: errorFields[0]?.errors[0] ?? 'Please fill all required fields' });
        }}
        scrollToFirstError
      >
        <UserInfo mode={mode} />
        {isCustomer && isRegularCustomer && (
          <RegularCustomerBusinessInfo
            isRegistered={isBusinessRegularCustomerRegistered}
            accountTypes={accountTypes}
            company_types={companyTypes}
            mode={mode}
          />
        )}
      </Form>
    </BaseCard>
  );
};

export default AccountInformation;
